<template>
  <div>
    <!-- ============================================================
       Dialog Thêm mới / Cập nhật khoản thu chính (tutition_v3)
       Sử dụng: <total-revenue-form-dialog ref="formDialog" @saved="fetchData" />
       Mở:     this.$refs.formDialog.openForCreate()
               this.$refs.formDialog.openForEdit(id)
       ============================================================ -->
    <dialog-base
      :visible.sync="visible"
      :title="isEditMode ? 'Cập nhật khoản thu chính' : 'Thêm mới khoản thu chính'"
      size="lg"
      cancel-text="Hủy"
      confirm-text="Lưu lại"
      :confirm-loading="loading"
      :use-panel="false"
      top="4vh"
      @confirm="handleSubmit"
      @cancel="handleClose"
    >
      <el-form
        ref="dataForm"
        :model="dataForm"
        :rules="formRules"
        label-width="210px"
        label-position="left"
        class="sk-dialog-content"
      >
        <div class="trf-help-entry">
          <el-tooltip content="Xem toàn bộ hướng dẫn thao tác" placement="top">
            <el-button type="primary" icon="el-icon-question" plain size="small" @click="openGuideDialog">
              Hướng dẫn thao tác
            </el-button>
          </el-tooltip>
        </div>
      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 1 – THÔNG TIN CHUNG
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon"><i class="el-icon-document" /></div>
          <div>
            <p class="sk-panel-title">Thông tin chung</p>
            <p class="sk-panel-subtitle">Tên, loại và mô tả khoản thu chính</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item prop="name">
              <template v-slot:label>
                Tên khoản thu
                <el-tooltip content="Tên hiển thị của khoản thu chính (ví dụ: Học phí tổng hợp)" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input v-model="dataForm.name" placeholder="Nhập tên khoản thu..." maxlength="255" show-word-limit />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="category">
              <template v-slot:label>
                Loại khoản
                <el-tooltip content="Chọn khoản thu hoặc khoản hoàn trả" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.category" placeholder="Chọn loại" class="sk-u-w-full" @change="onCategoryChange">
                <el-option label="Khoản thu" value="IN" />
                <el-option label="Khoản hoàn trả" value="OUT" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="unit">
              <template v-slot:label>
                Đơn vị tính
                <el-tooltip content="Chọn đơn vị hoặc nhập mới" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select
                v-model="dataForm.unit"
                filterable
                allow-create
                default-first-option
                placeholder="Chọn hoặc nhập đơn vị"
                class="sk-u-w-full"
              >
                <el-option v-for="u in unitsList" :key="u" :label="u" :value="u" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item>
              <template v-slot:label>
                Mô tả
                <el-tooltip content="Mô tả ngắn về khoản thu, tối đa 255 ký tự" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input v-model="dataForm.description" type="textarea" :rows="2" autosize placeholder="Nhập mô tả..." maxlength="255" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 2 – CÔNG THỨC / ĐƠN GIÁ
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--success"><i class="el-icon-money" /></div>
          <div>
            <p class="sk-panel-title">Tài chính & Công thức</p>
          </div>
        </div>

        <el-row :gutter="20">
          <!-- Áp dụng công thức -->
          <el-col :span="24">
            <el-form-item>
              <template v-slot:label>
                Áp dụng công thức
                <el-tooltip content="Bặt nếu muốn tính giá từ công thức tham chiếu các khoản thu gốc" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.hasFormula" @change="onHasFormulaChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <!-- ─ Nhánh: KHÔNG có công thức ─ -->
          <template>
            <el-col :span="12" v-if="!dataForm.hasFormula">
              <el-form-item prop="price">
                <template v-slot:label>
                  Đơn giá (VNĐ)
                  <el-tooltip content="Đơn giá cho 1 đơn vị tính (VNĐ)" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <ElCurrencyInput
                  v-model="dataForm.price"
                  :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                  class="sk-u-w-full"
                  @input="calculatePreview"
                />
              </el-form-item>
            </el-col>

            <el-col :span="12">
              <el-form-item prop="quantity">
                <template v-slot:label>
                  Số lượng
                  <el-tooltip content="Số lượng đơn vị (ví dụ: 1)" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input
                  v-model.number="dataForm.quantity"
                  type="number"
                  :step="allowDecimal ? 0.01 : 1"
                  class="sk-u-w-full"
                  @input="calculatePreview"
                />
              </el-form-item>
            </el-col>

            <el-col :span="12">
              <el-form-item label="Cho phép số thập phân">
                <el-switch
                  :value="allowDecimal"
                  active-text="Có"
                  inactive-text="Không"
                  @change="handleAllowDecimalChange"
                />
              </el-form-item>
            </el-col>

            <!-- Giảm giá -->
            <el-col :span="12">
              <el-form-item prop="discount">
                <template v-slot:label>
                  Giảm giá
                  <el-tooltip content="Bật nếu muốn áp dụng chính sách giảm giá" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-radio-group v-model="dataForm.discount" @change="onDiscountChange">
                  <el-radio :label="true">Có</el-radio>
                  <el-radio :label="false">Không</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>

            <el-col v-if="dataForm.discount" :span="12">
              <el-form-item prop="discountType">
                <template v-slot:label>
                  Loại giảm giá
                  <el-tooltip content="Phần trăm (%) hoặc số tiền (VNĐ)" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-select v-model="dataForm.discountType" placeholder="Chọn loại" class="sk-u-w-full" @change="calculatePreview">
                  <el-option label="Phần trăm (%)" value="PERCENT" />
                  <el-option label="Số tiền (VNĐ)" value="MONEY" />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col v-if="dataForm.discount" :span="12">
              <el-form-item prop="discountNumber">
                <template v-slot:label>
                  <span v-if="dataForm.discountType === 'PERCENT'">Phần trăm giảm (%)</span>
                  <span v-else>Số tiền giảm (VNĐ)</span>
                </template>
                <div class="flex items-center">
                  <template v-if="dataForm.discountType === 'MONEY'">
                    <ElCurrencyInput
                      v-model="dataForm.discountNumber"
                      :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                      class="sk-u-w-full"
                      @input="calculatePreview"
                    />
                  </template>
                  <template v-else>
                    <el-input-number v-discount-percent
                      v-model="dataForm.discountNumber"
                      :min="0"
                      :max="100"
                      :precision="0"
                      controls-position="right"
                      class="sk-u-w-full"
                      @change="calculatePreview"
                    />
                  </template>
                  <div v-if="discountAmount > 0" class="trf-discount-amount ml-3">-{{ formatMoney(discountAmount) }}</div>
                </div>
              </el-form-item>
            </el-col>

            <!-- Thành tiền preview -->
            <el-col :span="24" v-if="!dataForm.hasFormula">
              <el-form-item>
                <template v-slot:label>Thành tiền</template>
                <div class="trf-preview-money">{{ formatMoney(previewSumMoney) }}</div>
              </el-form-item>
            </el-col>

            <!-- Áp dụng thay đổi cho những khoản học sinh đã đăng ký-->
            <el-col :span="24">
              <el-form-item prop="discount">
                <template v-slot:label>
                  Áp dụng thay đổi
                  <el-tooltip content="Áp dụng thay đổi về số lượng, đơn giá, giảm giá, đơn vị tính và loại giảm cho những khoản học sinh đã đăng ký" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-radio-group v-model="dataForm.updateRegisteredRevenue">
                  <el-radio :label="true">Có</el-radio>
                  <el-radio :label="false">Không</el-radio>
                </el-radio-group>
              </el-form-item>
              <div
                class="trf-update-note"
                :class="dataForm.updateRegisteredRevenue ? 'trf-update-note--apply' : 'trf-update-note--skip'"
              >
                <i :class="dataForm.updateRegisteredRevenue ? 'el-icon-success' : 'el-icon-warning-outline'" />
                <span v-if="dataForm.updateRegisteredRevenue === true">Thay đổi sẽ được áp dụng với những khoản học sinh đã đăng ký đã khởi tạo.</span>
                <span v-else>Thay đổi sẽ không được áp dụng với những khoản học sinh đã đăng ký đã khởi tạo.</span>
              </div>
            </el-col>
          </template>

          <!-- ─ Nhánh: CÓ công thức ─ -->
          <template v-if="dataForm.hasFormula">
            <!-- BƯỚC 1: Khai báo biến công thức -->
            <el-col :span="24">
              <el-form-item>
                <template v-slot:label>
                  Khai báo biến công thức
                  <el-tooltip content="Khai báo tên biến (A-Z) và ánh xạ biến sang khoản thu gốc tương ứng" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>

                <!-- Header -->
                <el-row :gutter="10" class="trf-mapping-header mb-1">
                  <el-col :span="5"><span class="trf-col-label">Biến</span></el-col>
                  <el-col :span="17"><span class="trf-col-label">Khoản thu sử dụng để tính toán</span></el-col>
                  <el-col :span="2" />
                </el-row>

                <div v-if="dataForm.formulaMappings.length === 0" class="trf-empty-hint">
                  Nhấn "+ Thêm biến" để sử dụng trong biểu thức công thức.
                </div>

                <div v-for="(mapping, idx) in dataForm.formulaMappings" :key="idx" class="trf-mapping-row mb-2">
                  <el-row :gutter="10" align="middle" type="flex">
                    <el-col :span="5">
                      <el-input
                        v-model="mapping.alias"
                        placeholder="Ví dụ: A"
                        size="small"
                        maxlength="1"
                        style="text-transform:uppercase;"
                        @input="onMappingAliasInput(mapping)"
                      />
                    </el-col>
                    <el-col :span="17">
                      <el-select
                        v-model="mapping.idPrincipalRevenue"
                        filterable
                        placeholder="Chọn khoản thu sử dụng để tính "
                        size="small"
                        class="sk-u-w-full"
                        :loading="principalRevenueLoading"
                      >
                        <el-option
                          v-for="pr in principalRevenueOptions"
                          :key="pr.id"
                          :label="pr.name + ' — ' + formatMoney(pr.price)"
                          :value="pr.id"
                        />
                      </el-select>
                    </el-col>
                    <el-col :span="2" class="text-right">
                      <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeMapping(idx)" />
                    </el-col>
                  </el-row>
                </div>

                <el-button type="primary" size="small" icon="el-icon-plus" plain @click="addMapping">Thêm biến</el-button>
              </el-form-item>
            </el-col>

            <!-- BƯỚC 2: Nhập biểu thức -->
            <el-col :span="24">
              <el-form-item prop="formulaExpression">
                <template v-slot:label>
                  Biểu thức công thức
                  <el-tooltip content="Nhập công thức bằng các biến đã khai báo ở trên. Ví dụ: (A + B) * 0.9" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input
                  v-model="dataForm.formulaExpression"
                  placeholder="Ví dụ: (A + B) * 0.9"
                  maxlength="500"
                  show-word-limit
                  @input="onFormulaExpressionInput"
                />
                <div v-if="formulaUnknownAliases.length > 0" class="trf-formula-warning mt-2">
                  Biểu thức đang dùng biến chưa khai báo: {{ formulaUnknownAliases.join(', ') }}
                </div>
              </el-form-item>
            </el-col>

            <!-- formulaExpressionName: read-only từ response -->
            <el-col v-if="formulaExpressionName" :span="24">
              <el-form-item>
                <template v-slot:label>
                  Công thức (đã dịch)
                  <el-tooltip content="Công thức với tên khoản thu thay thế biến" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <div class="trf-formula-name">{{ formulaExpressionName }}</div>
              </el-form-item>
            </el-col>
          </template>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 3 – THUẾ & HÓA ĐƠN ĐIỆN TỬ
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--warning"><i class="el-icon-tickets" /></div>
          <div>
            <p class="sk-panel-title">Thuế & Hóa đơn</p>
            <p class="sk-panel-subtitle">Khai báo thuế GTGT và phát hành hóa đơn điện tử</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item>
              <template v-slot:label>
                Khai báo thuế
                <el-tooltip content="Bật nếu khoản thu này phải khai báo thuế GTGT" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.taxDeclaration" @change="onTaxDeclarationChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false" :disabled="dataForm.issuingElectronicInvoices">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <el-col v-if="dataForm.taxDeclaration" :span="12">
            <el-form-item prop="taxPercentage">
              <template v-slot:label>
                Thuế GTGT
                <el-tooltip content="Chọn loại thuế GTGT áp dụng" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select
                v-model="dataForm.taxPercentage"
                style="width: 100%"
                placeholder="-- Lựa chọn --"
                @change="handleTaxChange"
              >
                <el-option label="Khác" value="other" />
                <el-option label="Không chịu thuế" value="-2" />
                <el-option label="Không kê khai" value="-1" />
                <el-option label="0%" value="0" />
                <el-option label="5%" value="5" />
                <el-option label="8%" value="8" />
                <el-option label="10%" value="10" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col v-if="dataForm.taxDeclaration && String(dataForm.taxPercentage) === 'other'" :span="12">
            <el-form-item prop="taxPercentageInput">
              <template v-slot:label>
                Thuế khác (%)
                <el-tooltip content="Nhập phần trăm thuế tùy chỉnh" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input-number
                v-model="dataForm.taxPercentageInput"
                :controls="false"
                :min="0"
                class="sk-u-w-full"
                placeholder="Nhập %"
              />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item>
              <template v-slot:label>
                Hóa đơn điện tử
                <el-tooltip content="Bật nếu khoản thu cần phát hành hóa đơn điện tử" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.issuingElectronicInvoices" @change="onIssuingElectronicInvoicesChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 4 – THỜI GIAN ÁP DỤNG
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--warning"><i class="el-icon-date" /></div>
          <div>
            <p class="sk-panel-title">Thời gian áp dụng</p>
            <p class="sk-panel-subtitle">Cấu hình tháng, khoảng thời gian hoặc số đơn vị áp dụng</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item prop="appliedTimeType">
              <template v-slot:label>
                Kiểu thời gian
                <el-tooltip content="Loại chu kỳ áp dụng cho khoản thu" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.appliedTimeType" placeholder="Chọn kiểu thời gian" class="sk-u-w-full" @change="onAppliedTimeTypeChange">
                <el-option label="Theo tháng" value="MONTH" />
                <el-option label="Theo khoảng ngày" value="TIME_PERIOD" />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- MONTH: chọn tháng áp dụng -->
          <template v-if="dataForm.appliedTimeType === 'MONTH'">
            <el-col :span="24">
              <el-form-item>
                <template v-slot:label>
                  Tháng áp dụng
                  <el-tooltip content="Chọn các tháng trong năm mà khoản thu này được áp dụng" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <div class="trf-month-grid">
                  <div style="width:100%; display:flex; justify-content:flex-end; margin-bottom:6px;">
                    <el-checkbox v-model="monthsAll">Chọn tất cả</el-checkbox>
                  </div>
                  <el-checkbox v-for="m in monthList" :key="m.key" v-model="dataForm[m.key]" :label="m.label" border size="small" class="trf-month-cb" />
                </div>
              </el-form-item>
            </el-col>

            <!-- <el-col :span="24">
              <el-form-item>
                <template v-slot:label>
                  Chu kỳ
                  <el-tooltip content="Chu kỳ mặc định: cả tháng. Tùy chỉnh: theo ngày cụ thể" placement="top">
                    <i class="el-icon-question trf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-radio-group v-model="dataForm.defaultCycle">
                  <el-radio :label="true">Cả tháng</el-radio>
                  <el-radio :label="false">Tùy chỉnh ngày</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>

            <template v-if="dataForm.defaultCycle === false">
              <el-col :span="12">
                <el-form-item prop="startDay">
                  <template v-slot:label>
                    Ngày bắt đầu
                    <el-tooltip content="Ngày bắt đầu tính trong tháng (1–31)" placement="top">
                      <i class="el-icon-question trf-help-icon ml-2" />
                    </el-tooltip>
                  </template>
                  <el-input-number v-model="dataForm.startDay" :min="1" :max="31" :precision="0" controls-position="right" class="sk-u-w-full" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="endDay">
                  <template v-slot:label>
                    Ngày kết thúc
                    <el-tooltip content="Ngày kết thúc tính trong tháng (1–31)" placement="top">
                      <i class="el-icon-question trf-help-icon ml-2" />
                    </el-tooltip>
                  </template>
                  <el-input-number v-model="dataForm.endDay" :min="1" :max="31" :precision="0" controls-position="right" class="sk-u-w-full" />
                </el-form-item>
              </el-col>
            </template> -->
          </template>

          <!-- TIME_PERIOD: startDate / endDate -->
          <template v-if="dataForm.appliedTimeType === 'TIME_PERIOD'">
            <el-col :span="12">
              <el-form-item prop="startDate" label="Ngày bắt đầu">
                <el-date-picker
                  v-model="dataForm.startDate"
                  type="date"
                  placeholder="Chọn ngày bắt đầu"
                  value-format="yyyy-MM-dd"
                  class="sk-u-w-full"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="endDate" label="Ngày kết thúc">
                <el-date-picker
                  v-model="dataForm.endDate"
                  type="date"
                  placeholder="Chọn ngày kết thúc"
                  value-format="yyyy-MM-dd"
                  class="sk-u-w-full"
                />
              </el-form-item>
            </el-col>
          </template>

          <!-- numberUnit: luôn hiển thị -->
<!--          <el-col :span="12">-->
<!--            <el-form-item prop="numberUnit">-->
<!--              <template v-slot:label>-->
<!--                Số đơn vị-->
<!--                <el-tooltip content="Giới hạn số lượng đơn vị tối đa được áp dụng cho khoản" placement="top">-->
<!--                  <i class="el-icon-question trf-help-icon ml-2" />-->
<!--                </el-tooltip>-->
<!--              </template>-->
<!--              <el-input-number v-model="dataForm.numberUnit" :min="0" :precision="0" controls-position="right" class="sk-u-w-full" />-->
<!--            </el-form-item>-->
<!--          </el-col>-->
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 5 – CẤU HÌNH ĐIỂM DANH
           Chỉ hiển thị khi hasFormula = false
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--danger"><i class="el-icon-user" /></div>
          <div>
            <p class="sk-panel-title">Cấu hình điểm danh</p>
            <p class="sk-panel-subtitle">Thiết lập tính phí theo điểm danh thực tế </p>
          </div>
        </div>

        <el-row :gutter="20">
          <!-- Áp dụng điểm danh -->
          <el-col :span="12">
            <el-form-item prop="attendance">
              <template v-slot:label>
                Áp dụng điểm danh
                <el-tooltip content="Bật nếu khoản thu tính dựa trên điểm danh thực tế" placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.attendance" @change="onAttendanceChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <!-- Phương pháp tính – chỉ hiển thị với Khoản thu (IN), OUT luôn là AFTER -->
          <el-col v-if="dataForm.attendance" :span="12">
            <el-form-item prop="calculationMethod">
              <template v-slot:label>
                Phương pháp tính
                <el-tooltip content="Tính trước: tính theo lịch học. Tính sau: tính theo điểm danh thực tế." placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group
                v-model="dataForm.calculationMethod"
                :disabled="dataForm.category === 'OUT'"
                @change="resetAttendanceMethod"
              >
                <el-radio label="BEFORE" :disabled="dataForm.category === 'OUT'">Tính trước</el-radio>
                <el-radio label="AFTER">Tính sau</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <!-- Loại điểm danh – options phụ thuộc category + calculationMethod -->
          <el-col v-if="dataForm.attendance && attendanceTypeCurrentList.length > 0" :span="12">
            <el-form-item prop="attendanceType">
              <template v-slot:label>
                Loại điểm danh
                <el-tooltip content="Chọn loại điểm danh tương ứng với phương pháp tính." placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select
                v-model="dataForm.attendanceType"
                placeholder="Chọn loại điểm danh"
                class="sk-u-w-full"
                @change="changeAttendanceTypeMethod"
              >
                <el-option
                  v-for="item in attendanceTypeCurrentList"
                  :key="item.key"
                  :value="item.key"
                  :label="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Chi tiết (configKey) – hiển thị khi có danh sách chi tiết -->
          <el-col v-if="dataForm.attendance && configKeyCurrentList.length > 0" :span="12">
            <el-form-item prop="configKey">
              <template v-slot:label>
                Chi tiết
                <el-tooltip content="Chọn chi tiết tương ứng với loại điểm danh." placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.configKey" placeholder="Chọn chi tiết" class="sk-u-w-full">
                <el-option
                  v-for="item in configKeyCurrentList"
                  :key="item.key"
                  :value="item.key"
                  :label="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Giờ đón về – chỉ hiển thị khi IN + AFTER + LATE -->
          <el-col v-if="dataForm.attendance && dataForm.category === 'IN' && dataForm.calculationMethod === 'AFTER' && dataForm.attendanceType === 'LATE'" :span="12">
            <el-form-item>
              <template v-slot:label>
                Giờ đón về
                <el-tooltip content="Thời gian đón về chuẩn (giờ:phút), dùng để xác định khung đón muộn." placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-time-picker
                v-model="dataForm.configTime"
                format="HH:mm"
                :picker-options="{ format: 'HH:mm' }"
                value-format="HH:mm"
                placeholder="Chọn giờ đón về"
                class="sk-u-w-full"
              />
            </el-form-item>
          </el-col>

          <!-- Cấu hình đón muộn – chỉ hiển thị khi IN + AFTER + LATE -->
          <el-col v-if="dataForm.attendance && dataForm.category === 'IN' && dataForm.calculationMethod === 'AFTER' && dataForm.attendanceType === 'LATE'" :span="24">
            <el-form-item>
              <template v-slot:label>
                Cấu hình đón muộn
                <el-tooltip content="Mỗi block tương ứng 1 khoảng thời gian đón muộn và mức thu tiền tương ứng." placement="top">
                  <i class="el-icon-question trf-help-icon ml-2" />
                </el-tooltip>
              </template>

              <!-- Header labels -->
              <el-row :gutter="10" class="trf-config-header mb-1">
                <el-col :span="6"><span class="trf-col-label">Tên block</span></el-col>
                <el-col :span="6"><span class="trf-col-label">Từ giờ</span></el-col>
                <el-col :span="6"><span class="trf-col-label">Đến giờ</span></el-col>
                <el-col :span="5"><span class="trf-col-label">Số tiền (VNĐ)</span></el-col>
                <el-col :span="1" />
              </el-row>

              <div v-if="dataForm.configList.length === 0" class="trf-empty-hint">Chưa có cấu hình. Nhấn "+ Thêm" để bắt đầu.</div>

              <div v-for="(cfg, idx) in dataForm.configList" :key="idx" class="trf-config-row mb-2">
                <el-row :gutter="10" align="middle" type="flex">
                  <el-col :span="6">
                    <el-input v-model="cfg.configName" placeholder="Tên (vd: Muộn 30 phút)" size="small" />
                  </el-col>
                  <el-col :span="6">
                    <el-time-picker
                      v-model="cfg.configStartTime"
                      format="HH:mm"
                      :picker-options="{ format: 'HH:mm' }"
                      value-format="HH:mm"
                      placeholder="Từ giờ"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <el-col :span="6">
                    <el-time-picker
                      v-model="cfg.configEndTime"
                      format="HH:mm"
                      :picker-options="{ format: 'HH:mm' }"
                      value-format="HH:mm"
                      placeholder="Đến giờ"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <el-col :span="5">
                    <ElCurrencyInput
                      v-model="cfg.configValue"
                      :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <el-col :span="1" class="text-right">
                    <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeConfig(idx)" />
                  </el-col>
                </el-row>
              </div>

              <el-button type="primary" size="small" icon="el-icon-plus" plain @click="addConfig">Thêm khoảng thời gian</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </div>
      </el-form>
    </dialog-base>

    <total-revenue-guide-dialog ref="guideDialog" />
  </div>
</template>

<script>
import { discountPercentInput, hasOverLimitPercentDiscount, DISCOUNT_PERCENT_LIMIT_MESSAGE } from "@/utils/discountPercent";
import DialogBase from "@/components/DialogBase.vue";
import ElCurrencyInput from "@/components/ElCurrencyInput.vue";
import TotalRevenueGuideDialog from "@/components/tutition_v3/dialog/TotalRevenueGuideDialog.vue";
import ManagingTotalRevenueService from "@/services/tutition_v3/ManagingTotalRevenueService";
import ManagingPrincipalRevenueService from "@/services/tutition_v3/ManagingPrincipalRevenueService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import {
  hasQuantityFraction,
  isQuantityPrecisionValid,
  toQuantityJsonNumber,
} from "@/components/tutition_v3/utils/quantity";
import {
  ATTEND_TYPE_IN_AFTER,
  ATTEND_TYPE_IN_BEFORE,
  ATTEND_TYPE_OUT_AFTER, CONFIG_KEY_IN_AFTER_LEARN, CONFIG_KEY_IN_BEFORE_EAT, CONFIG_KEY_IN_BEFORE_LEARN,
  CONFIG_KEY_OUT_AFTER_EAT,
  CONFIG_KEY_OUT_AFTER_LEARN,
  DEFAULT_UNITS,
  MONTH_LIST
} from "@/constant/tuitionConstant";

const defaultDataForm = () => ({
  // Nhóm 1 – Thông tin chung
  name: "", category: "IN", description: "", unit: "",

  // Nhóm 2 – Công thức / Đơn giá
  hasFormula: false,
  price: 0, quantity: 1,
  formulaExpression: "",
  formulaMappings: [],   // [{ alias, idPrincipalRevenue }]

  // Giảm giá (chỉ dùng khi hasFormula = false)
  discount: false, discountType: "PERCENT", discountNumber: 0,

  // Nhóm 3 – Thuế & Hóa đơn
  taxDeclaration: false, taxPercentage: "", taxPercentageInput: null,
  issuingElectronicInvoices: false,

  // Nhóm 4 – Thời gian áp dụng
  appliedTimeType: "MONTH",
  t1: false, t2: false, t3: false, t4: false,
  t5: false, t6: false, t7: false, t8: false,
  t9: false, t10: false, t11: false, t12: false,
  // defaultCycle: true, startDay: 1, endDay: 31,
  startDate: null, endDate: null, numberUnit: null,

  // Nhóm 5 – Điểm danh (chỉ khi hasFormula = false)
  attendance: false,
  calculationMethod: "BEFORE",
  attendanceType: null,
  configKey: "",
  attendanceVersion: "VERSION_ONESCHOOL",
  configValue: null,
  configTime: null,
  configList: [],
  updateRegisteredRevenue: false,
});

export default {
  directives: { discountPercent: discountPercentInput },
  name: "TotalRevenueFormDialog",
  components: { DialogBase, ElCurrencyInput, TotalRevenueGuideDialog },

  data() {
    const validateFormula = (rule, value, callback) => {
      if (this.dataForm.hasFormula && (!value || String(value).trim() === "")) {
        return callback(new Error("Vui lòng nhập biểu thức công thức"));
      }
      const syntaxError = this._validateFormulaSyntax(value);
      if (this.dataForm.hasFormula && syntaxError) {
        return callback(new Error(syntaxError));
      }
      if (this.dataForm.hasFormula && this.formulaUnknownAliases.length > 0) {
        return callback(new Error(`Biểu thức có biến chưa khai báo: ${this.formulaUnknownAliases.join(", ")}`));
      }
      return callback();
    };
    const validateTaxPercentage = (rule, value, callback) => {
      if (!this.dataForm.taxDeclaration) return callback();
      if (value === undefined || value === null || String(value).trim() === "") {
        return callback(new Error("Vui lòng chọn thuế GTGT"));
      }
      return callback();
    };
    const validateTaxPercentageInput = (rule, value, callback) => {
      if (!this.dataForm.taxDeclaration || String(this.dataForm.taxPercentage) !== "other") return callback();
      const custom = Number(value);
      if (!Number.isFinite(custom) || custom < 0) {
        return callback(new Error("Vui lòng nhập thuế GTGT hợp lệ"));
      }
      return callback();
    };
    const validateQuantity = (rule, value, callback) => {
      const v = Number(value);
      if (!isQuantityPrecisionValid(value, this.allowDecimal)) {
        callback(new Error(this.allowDecimal
          ? "Số lượng chỉ được có tối đa 2 chữ số thập phân"
          : "Số lượng phải là số nguyên"));
      } else if (this.dataForm.category === 'IN' && v <= 0) {
        callback(new Error("Khoản thu phải có số lượng > 0"));
      } else if (this.dataForm.category === 'OUT' && v >= 0) {
        callback(new Error("Khoản hoàn trả phải có số lượng < 0"));
      } else {
        callback();
      }
    };
    const validateDiscountType = (rule, value, callback) => {
      if (this.dataForm.discount && !this.dataForm.hasFormula && !value) {
        callback(new Error("Vui lòng chọn loại giảm giá"));
      } else {
        callback();
      }
    };
    const validateDiscountNumber = (rule, value, callback) => {
      if (!this.dataForm.discount || this.dataForm.hasFormula) return callback();
      if (value === null || value === undefined || value === "") return callback(new Error("Vui lòng nhập giá trị giảm"));
      if (this.dataForm.discountType === "PERCENT") {
        const v = Number(value) || 0;
        if (v < 0 || v > 100) return callback(new Error("Phần trăm giảm phải trong khoảng 0–100"));
      }
      callback();
    };
    const validateStartDate = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "TIME_PERIOD" && !value) callback(new Error("Vui lòng chọn ngày bắt đầu"));
      else callback();
    };
    const validateEndDate = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "TIME_PERIOD" && !value) callback(new Error("Vui lòng chọn ngày kết thúc"));
      else callback();
    };
    const validateNumberUnit = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "UNIT" && (!value || value < 1)) callback(new Error("Vui lòng nhập số đơn vị >= 1"));
      else callback();
    };
    const validateAttendanceType = (rule, value, callback) => {
      if (this.dataForm.attendance && !this.dataForm.hasFormula && !value) callback(new Error("Vui lòng chọn loại điểm danh"));
      else callback();
    };
    const validateCalculationMethod = (rule, value, callback) => {
      if (this.dataForm.attendance && !this.dataForm.hasFormula && !value) callback(new Error("Vui lòng chọn phương pháp tính"));
      else callback();
    };
    const validateConfigKey = (rule, value, callback) => {
      if (
        this.dataForm.attendance &&
        !this.dataForm.hasFormula &&
        this.dataForm.attendanceType &&
        this.configKeyCurrentList.length > 0 &&
        !value
      ) {
        callback(new Error("Vui lòng chọn chi tiết điểm danh"));
      } else {
        callback();
      }
    };

    return {
      MONTH_LIST,
      DEFAULT_UNITS,
      ATTEND_TYPE_IN_BEFORE,
      ATTEND_TYPE_IN_AFTER,
      ATTEND_TYPE_OUT_AFTER,
      CONFIG_KEY_IN_BEFORE_LEARN,
      CONFIG_KEY_IN_BEFORE_EAT,
      CONFIG_KEY_IN_AFTER_LEARN,
      CONFIG_KEY_OUT_AFTER_LEARN,
      CONFIG_KEY_OUT_AFTER_EAT,
      visible: false,
      loading: false,
      isEditMode: false,
      editingId: null,
      allowDecimal: false,
      suppressAttendanceWatch: false,
      previewSumMoney: 0,
      discountAmount: 0,
      formulaExpressionName: "",   // read-only, từ response của BE
      monthList: MONTH_LIST,
      unitsList: [...DEFAULT_UNITS],
      principalRevenueOptions: [],
      principalRevenueLoading: false,
      dataForm: defaultDataForm(),
      formRules: {
        name:                [{ required: true, message: "Vui lòng nhập tên khoản thu", trigger: "blur" }],
        category:            [{ required: true, message: "Vui lòng chọn loại khoản", trigger: "change" }],
        quantity:            [{ required: true, message: "Vui lòng nhập số lượng", trigger: "blur" }, { validator: validateQuantity, trigger: "blur" }],
        formulaExpression:   [{ validator: validateFormula,             trigger: "blur" }],
        taxPercentage:       [{ validator: validateTaxPercentage,       trigger: "change" }],
        taxPercentageInput:  [{ validator: validateTaxPercentageInput,  trigger: "change" }],
        discountType:        [{ validator: validateDiscountType,        trigger: "change" }],
        discountNumber:      [{ validator: validateDiscountNumber,      trigger: "blur" }],
        startDate:           [{ validator: validateStartDate,           trigger: "change" }],
        endDate:             [{ validator: validateEndDate,             trigger: "change" }],
        numberUnit:          [{ validator: validateNumberUnit,          trigger: "blur" }],
        attendanceType:      [{ validator: validateAttendanceType,      trigger: "change" }],
        calculationMethod:   [{ validator: validateCalculationMethod,   trigger: "change" }],
        configKey:           [{ validator: validateConfigKey,           trigger: "change" }],
        price: [
          { required: true, message: "Vui lòng nhập đơn giá", trigger: "blur" },
          { type: "number", min: 0, message: "Đơn giá phải >= 0", trigger: "blur" },
        ],
      },
    };
  },

  computed: {
    monthsAll: {
      get() {
        return this.monthList.every(m => !!this.dataForm[m.key]);
      },
      set(val) {
        this.monthList.forEach(m => { this.$set(this.dataForm, m.key, !!val); });
      },
    },

    // Danh sách loại điểm danh phụ thuộc category + calculationMethod
    attendanceTypeCurrentList() {
      const { category, calculationMethod } = this.dataForm;
      if (category === "IN") {
        return calculationMethod === "BEFORE" ? ATTEND_TYPE_IN_BEFORE : ATTEND_TYPE_IN_AFTER;
      }
      if (category === "OUT") {
        return ATTEND_TYPE_OUT_AFTER;
      }
      return [];
    },

    // Danh sách chi tiết (configKey) phụ thuộc category + calculationMethod + attendanceType
    configKeyCurrentList() {
      const { category, calculationMethod, attendanceType } = this.dataForm;
      if (category === "IN" && calculationMethod === "BEFORE") {
        if (attendanceType === "LEARN") return CONFIG_KEY_IN_BEFORE_LEARN;
        if (attendanceType === "EAT")   return CONFIG_KEY_IN_BEFORE_EAT;
      }
      if (category === "IN" && calculationMethod === "AFTER") {
        if (attendanceType === "LEARN") return CONFIG_KEY_IN_AFTER_LEARN;
        // LATE → dùng configList block
      }
      if (category === "OUT") {
        if (attendanceType === "LEARN") return CONFIG_KEY_OUT_AFTER_LEARN;
        if (attendanceType === "EAT")   return CONFIG_KEY_OUT_AFTER_EAT;
      }
      return [];
    },

    // Computed để watch đồng thời các trường ảnh hưởng đến configKey
    attendanceContext() {
      return [this.dataForm.attendanceType, this.dataForm.calculationMethod, this.dataForm.category];
    },

    // Danh sách biến xuất hiện trong biểu thức nhưng chưa khai báo ở mapping
    formulaUnknownAliases() {
      const declared = new Set(
        this.dataForm.formulaMappings
          .map(m => String(m.alias || "").trim().toUpperCase())
          .filter(alias => /^[A-Z]$/.test(alias))
      );
      const used = this._extractFormulaAliases(this.dataForm.formulaExpression);
      return used.filter(alias => !declared.has(alias));
    },
  },

  watch: {
    "dataForm.price":          "calculatePreview",
    "dataForm.quantity":       "calculatePreview",
    "dataForm.discount":       "calculatePreview",
    "dataForm.discountType":   "calculatePreview",
    "dataForm.discountNumber": "calculatePreview",
    "dataForm.unit"(val) {
      if (!val) return;
      if (!this.unitsList.includes(val)) this.unitsList.push(val);
    },
    attendanceContext(newVal, oldVal) {
      if (this.suppressAttendanceWatch) return;
      if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1] || newVal[2] !== oldVal[2]) {
        this.dataForm.configKey = "";
      }
    },
  },

  methods: {
    // ─── API công khai ──────────────────────────────────────────────
    openForCreate() {
      this.isEditMode = false;
      this.editingId = null;
      this.allowDecimal = false;
      this.dataForm = defaultDataForm();
      this.previewSumMoney = 0;
      this.discountAmount = 0;
      this.formulaExpressionName = "";
      this.visible = true;
      this._loadPrincipalRevenueOptions();
      this.$nextTick(() => this.$refs.dataForm?.clearValidate());
    },

    async openForEdit(id) {
      this.isEditMode = true;
      this.editingId = id;
      this.allowDecimal = false;
      this.dataForm = defaultDataForm();
      this.previewSumMoney = 0;
      this.discountAmount = 0;
      this.formulaExpressionName = "";
      this.visible = true;
      await this._loadPrincipalRevenueOptions();

      try {
        const { data } = await ManagingTotalRevenueService.findById(id);
        const d = data.data;

        this.formulaExpressionName = d.formulaExpressionName || "";

        // Tắt watch attendanceContext khi đang load để tránh reset configKey
        this.suppressAttendanceWatch = true;

        let inferredAttendanceType = d.attendanceType || this._getInferredAttendanceType(d);
        if (inferredAttendanceType === "PICKUP_LATE") {
          inferredAttendanceType = "LATE";
        }

        let taxPercentageVal = d.taxPercentage !== undefined && d.taxPercentage !== null ? String(d.taxPercentage) : "";
        let taxPercentageInputVal = d.taxPercentageInput ?? null;
        if (d.taxDeclaration && taxPercentageVal !== "") {
          const standardTaxes = ["-2", "-1", "0", "5", "8", "10"];
          if (!standardTaxes.includes(taxPercentageVal)) {
            taxPercentageInputVal = d.taxPercentageInput !== null ? d.taxPercentageInput : Number(taxPercentageVal);
            taxPercentageVal = "other";
          }
        }

        this.dataForm = {
          name:                      d.name                      || "",
          category:                  d.category                  || "IN",
          description:               d.description               || "",
          unit:                      d.unit                      || "",

          hasFormula:                d.hasFormula                ?? false,
          price:                     d.price                     ?? 0,
          quantity:                  d.quantity === undefined ? 1 : d.quantity,
          formulaExpression:         d.formulaExpression         || "",
          formulaMappings: Array.isArray(d.formulaMappings)
            ? d.formulaMappings.map(m => ({
                alias: m.alias || "",
                idPrincipalRevenue: m.idPrincipalRevenue || null,
              }))
            : [],

          discount:       d.discount       ?? false,
          discountType:   d.discountType   || "PERCENT",
          discountNumber: d.discountNumber ?? 0,

          taxDeclaration:             d.taxDeclaration            ?? false,
          taxPercentage:              taxPercentageVal,
          taxPercentageInput:         taxPercentageInputVal,
          issuingElectronicInvoices:  d.issuingElectronicInvoices ?? false,

          appliedTimeType: d.appliedTimeType || "MONTH",
          t1:  d.t1  ?? false, t2:  d.t2  ?? false, t3:  d.t3  ?? false,
          t4:  d.t4  ?? false, t5:  d.t5  ?? false, t6:  d.t6  ?? false,
          t7:  d.t7  ?? false, t8:  d.t8  ?? false, t9:  d.t9  ?? false,
          t10: d.t10 ?? false, t11: d.t11 ?? false, t12: d.t12 ?? false,
          // defaultCycle: d.defaultCycle ?? true,
          // startDay:     d.startDay     ?? 1,
          // endDay:       d.endDay       ?? 31,
          startDate:    d.startDate    || null,
          endDate:      d.endDate      || null,
          numberUnit:   d.numberUnit   || null,

          attendance:         d.attendance         ?? false,
          calculationMethod:  d.calculationMethod  || "BEFORE",
          attendanceType:     inferredAttendanceType,
          configKey:          d.configKey          || "",
          attendanceVersion:  d.attendanceVersion  || "VERSION_ONESCHOOL",
          configValue:        d.configValue        ?? null,
          configTime:         d.configTime         || null,
          configList: Array.isArray(d.configList)
            ? d.configList.map(c => ({
                configName:      c.configName      || "",
                configStartTime: c.configStartTime || "",
                configEndTime:   c.configEndTime   || "",
                configValue:     c.configValue     ?? 0,
              }))
            : [],
          updateRegisteredRevenue: false,
        };
        this.allowDecimal = hasQuantityFraction(this.dataForm.quantity);

        this.$nextTick(() => {
          this.suppressAttendanceWatch = false;
          this.calculatePreview();
          this.$refs.dataForm?.clearValidate();
        });
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải thông tin khoản thu chính!"
        );
        this.visible = false;
      }
    },

    // ─── Tải danh sách khoản thu gốc cho selector ──────────────────
    async _loadPrincipalRevenueOptions() {
      if (this.principalRevenueOptions.length > 0) return;
      this.principalRevenueLoading = true;
      try {
        const res = await ManagingPrincipalRevenueService.findActivePrincipalRevenues();
        const pageData = res.data.data;
        this.principalRevenueOptions = Array.isArray(pageData)
          ? pageData
          : (Array.isArray(pageData?.content) ? pageData.content : []);
      } catch {
        // silent
      } finally {
        this.principalRevenueLoading = false;
      }
    },

    // ─── Submit ─────────────────────────────────────────────────────
    async handleSubmit() {
      try { await this.$refs.dataForm.validate(); } catch { return; }

      // Kiểm tra formulaMappings khi hasFormula = true
      if (this.dataForm.hasFormula) {
        const normalized = this.dataForm.formulaMappings.map(m => ({
          alias: String(m.alias || "").trim().toUpperCase(),
          idPrincipalRevenue: m.idPrincipalRevenue,
        }));
        const aliasSet = new Set();
        const invalid = normalized.some(m => {
          const aliasInvalid = !/^[A-Z]$/.test(m.alias);
          const duplicated = aliasSet.has(m.alias);
          aliasSet.add(m.alias);
          return aliasInvalid || duplicated || !m.idPrincipalRevenue;
        });
        if (this.dataForm.formulaMappings.length === 0 || invalid) {
          NotifyMessage.leftCornerNotificationError("Vui lòng hoàn thiện đầy đủ thông tin mapping công thức (biến và khoản thu gốc)!");
          return;
        }
        if (this.formulaUnknownAliases.length > 0) {
          NotifyMessage.leftCornerNotificationError(`Biểu thức có biến chưa khai báo: ${this.formulaUnknownAliases.join(", ")}`);
          return;
        }
        const syntaxError = this._validateFormulaSyntax(this.dataForm.formulaExpression);
        if (syntaxError) {
          NotifyMessage.leftCornerNotificationError(syntaxError);
          return;
        }
      }

      this.loading = true;
      try {
        const payload = this._buildPayload();
        if (hasOverLimitPercentDiscount(payload)) {
          NotifyMessage.leftCornerNotificationError(DISCOUNT_PERCENT_LIMIT_MESSAGE);
          return;
        }

        if (this.isEditMode) {
          const res = await ManagingTotalRevenueService.update(this.editingId, payload);
          NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Cập nhật khoản thu chính thành công!");
        } else {
          const res = await ManagingTotalRevenueService.create(payload);
          NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Thêm mới khoản thu chính thành công!");
        }
        this.visible = false;
        this.$emit("saved");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Lưu khoản thu chính thất bại!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleClose() { this.visible = false; },

    openGuideDialog() {
      this.$refs.guideDialog?.open();
    },

    handleAllowDecimalChange(value) {
      if (!value && hasQuantityFraction(this.dataForm.quantity)) {
        NotifyMessage.leftCornerNotificationWarning(
          "Vui lòng sửa số lượng về số nguyên trước khi tắt chế độ thập phân."
        );
        return;
      }
      this.allowDecimal = value;
      this.$nextTick(() => this.$refs.dataForm?.validateField("quantity"));
    },

    // ─── Build payload theo API docs ────────────────────────────────
    _buildPayload() {
      const f = this.dataForm;
      // ⚠️ KHÔNG gửi: idSchool, idCreated, createdBy, idModified, lastModifiedBy
      const payload = {
        // Nhóm 1
        name:        f.name,
        category:    f.category,
        description: f.description || null,
        unit:        f.unit || null,
        ...(this.isEditMode ? { updateRegisteredRevenue: f.updateRegisteredRevenue } : {}),
        // Nhóm 2 – Công thức / Đơn giá
        hasFormula: f.hasFormula,
        quantity:   toQuantityJsonNumber(f.quantity),
        ...(f.hasFormula
          ? {
              // hasFormula = true → gửi công thức, bỏ price
              formulaExpression: f.formulaExpression,
              formulaMappings: f.formulaMappings.map(m => ({
                alias:             String(m.alias || "").trim().toUpperCase(),
                idPrincipalRevenue: m.idPrincipalRevenue,
              })),
              discount:       f.discount,
              discountType:   f.discount ? f.discountType   : null,
              discountNumber: f.discount ? f.discountNumber : null,
            }
          : {
              // hasFormula = false → gửi price, discount, configList
              price:          f.price,
              discount:       f.discount,
              discountType:   f.discount ? f.discountType   : null,
              discountNumber: f.discount ? f.discountNumber : null,
            }),

        // Nhóm 3 – Thuế & Hóa đơn
        taxDeclaration:            f.taxDeclaration,
        taxPercentage:             f.taxDeclaration ? (String(f.taxPercentage) === "other" ? f.taxPercentageInput : f.taxPercentage) : null,
        taxPercentageInput:        f.taxDeclaration && String(f.taxPercentage) === "other" ? f.taxPercentageInput : null,
        issuingElectronicInvoices: f.issuingElectronicInvoices,

        // Nhóm 4 – Thời gian (xử lý bên dưới)
        appliedTimeType: f.appliedTimeType || null,

        // Nhóm 5 – Điểm danh (chỉ khi hasFormula = false)
        attendance:        f.attendance,
        attendanceType:    f.attendance ? f.attendanceType : null,
        attendanceVersion: f.attendance ? (f.attendanceVersion || "VERSION_ONESCHOOL") : null,
        calculationMethod: f.attendance ? (f.calculationMethod || "BEFORE") : null,
        configKey:         f.attendance
          ? (f.attendanceType === "LATE"
              ? "LATE"
              : (this.configKeyCurrentList.length > 0 ? (f.configKey || null) : null))
          : null,
        configValue:       (f.attendance) ? (f.configValue ?? null) : null,
        configTime:        (f.attendance && f.attendanceType === "LATE") ? (f.configTime || null) : null,
        configList:        (f.attendance && f.attendanceType === "LATE" && Array.isArray(f.configList)) ? f.configList : [],
      };

      if (f.appliedTimeType === "MONTH") {
        MONTH_LIST.forEach(m => { payload[m.key] = f[m.key]; });
        // payload.defaultCycle = f.defaultCycle;
        // payload.startDay     = f.defaultCycle ? null : f.startDay;
        // payload.endDay       = f.defaultCycle ? null : f.endDay;
        payload.startDate = null; payload.endDate = null;
      } else if (f.appliedTimeType === "TIME_PERIOD") {
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null; payload.startDay = null; payload.endDay = null;
        payload.startDate = f.startDate; payload.endDate = f.endDate;
      } else {
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null; payload.startDay = null; payload.endDay = null;
        payload.startDate = null; payload.endDate = null;
      }

      // numberUnit luôn gửi đi, không phụ thuộc appliedTimeType
      payload.numberUnit = f.numberUnit;

      return payload;
    },

    // ─── Tính tiền tại FE (preview) ─────────────────────────────────
    calculatePreview() {
      if (this.dataForm.hasFormula) {
        this.discountAmount = 0;
        this.previewSumMoney = 0;
        return;
      }
      const { price, quantity, discount, discountType, discountNumber } = this.dataForm;
      const base = (Number(price) || 0) * (Number(quantity) || 0);
      if (!discount) {
        this.discountAmount = 0;
        this.previewSumMoney = base;
        return;
      }
      if (discountType === "PERCENT") {
        const pct = Number(discountNumber) || 0;
        this.discountAmount = Math.round(base * pct / 100);
      } else {
        this.discountAmount = Number(discountNumber) || 0;
      }
      if (this.discountAmount > base) {
        this.$alert("Số tiền giảm không thể lớn hơn tổng tiền!", "Thông báo", {
          confirmButtonText: "Đã hiểu",
          callback: () => {
            this.dataForm.discountNumber = 0;
            this.discountAmount = 0;
            this.calculatePreview();
          },
        });
        this.previewSumMoney = base;
      } else {
        this.previewSumMoney = base - this.discountAmount;
      }
    },

    // ─── Formula helpers ─────────────────────────────────────────────
    _extractFormulaAliases(expression) {
      // Chỉ nhận biến 1 ký tự từ A-Z, không tự thêm/xóa mapping
      const expr = String(expression || "");
      const matches = expr.match(/\b[a-zA-Z]\b/g) || [];
      return [...new Set(matches.map(ch => ch.toUpperCase()))];
    },

    _validateFormulaSyntax(expression) {
      if (!this.dataForm.hasFormula) return "";

      const raw = String(expression || "").toUpperCase();
      const expr = raw.replace(/\s+/g, "");
      if (!expr) return "";

      // Chỉ cho phép: biến A-Z, số, + - * / và ngoặc tròn
      if (!/^[A-Z0-9+\-*/().]+$/.test(expr)) {
        return "Biểu thức chỉ được dùng biến A-Z, số, +, -, *, / và dấu ngoặc ()";
      }

      // Token hợp lệ: biến 1 ký tự, số thập phân, toán tử, ngoặc
      const tokens = expr.match(/([A-Z]|\d+(?:\.\d+)?|[+\-*/()])/g) || [];
      if (tokens.join("") !== expr) {
        return "Biểu thức có ký tự hoặc cú pháp không hợp lệ";
      }

      let depth = 0;
      let expectOperand = true;
      for (const token of tokens) {
        const isAlias = /^[A-Z]$/.test(token);
        const isNumber = /^\d+(?:\.\d+)?$/.test(token);
        const isOperator = /^[+\-*/]$/.test(token);

        if (expectOperand) {
          if (isAlias || isNumber) {
            expectOperand = false;
            continue;
          }
          if (token === "(") {
            depth += 1;
            continue;
          }
          return "Biểu thức sai cú pháp: thiếu toán hạng hoặc mở ngoặc";
        }

        if (isOperator) {
          expectOperand = true;
          continue;
        }
        if (token === ")") {
          depth -= 1;
          if (depth < 0) return "Biểu thức sai cú pháp: ngoặc đóng không hợp lệ";
          continue;
        }
        return "Biểu thức sai cú pháp: thiếu toán tử";
      }

      if (depth !== 0) return "Biểu thức sai cú pháp: ngoặc chưa đóng đủ";
      if (expectOperand) return "Biểu thức sai cú pháp: kết thúc bằng toán tử";
      if (/\(\)/.test(expr)) return "Biểu thức sai cú pháp: không được để ngoặc rỗng ()";

      return "";
    },

    onFormulaExpressionInput() {
      this.$nextTick(() => this.$refs.dataForm?.validateField("formulaExpression"));
    },

    // ─── Sự kiện field ──────────────────────────────────────────────
    onHasFormulaChange(val) {
      if (val) {
        // Chuyển sang Áp dụng công thức → reset price-related
        this.dataForm.price = 0;
        this.dataForm.discount = false;
        this.dataForm.discountType = "PERCENT";
        this.dataForm.discountNumber = 0;
        this.dataForm.attendance = false;
        this.dataForm.attendanceType = null;
        this.dataForm.configList = [];
        this.previewSumMoney = 0;
        this.discountAmount = 0;
        this._loadPrincipalRevenueOptions();
      } else {
        this.dataForm.formulaExpression = "";
        this.dataForm.formulaMappings = [];
        this.formulaExpressionName = "";
      }
    },

    onTaxDeclarationChange(val) {
      if (!val) {
        this.dataForm.taxPercentage = "";
        this.dataForm.taxPercentageInput = null;
      }
    },

    onIssuingElectronicInvoicesChange(val) {
      if (val) {
        // Hóa đơn điện tử = Có → bắt buộc khai báo thuế
        this.dataForm.taxDeclaration = true;
      }
    },

    handleTaxChange(value) {
      if (String(value) !== "other") {
        this.dataForm.taxPercentageInput = null;
      }
      this.$nextTick(() => {
        if (this.$refs.dataForm) {
          this.$refs.dataForm.validateField("taxPercentage");
          this.$refs.dataForm.validateField("taxPercentageInput");
        }
      });
    },

    onDiscountChange(val) {
      if (!val) {
        this.dataForm.discountType = "PERCENT";
        this.dataForm.discountNumber = 0;
      }
      this.calculatePreview();
    },

    onAttendanceChange(val) {
      if (!val) {
        this.dataForm.calculationMethod = "BEFORE";
        this.dataForm.attendanceType = null;
        this.dataForm.configKey = "";
        this.dataForm.configValue = null;
        this.dataForm.configTime = null;
        this.dataForm.configList = [];
      }
    },

    onCategoryChange(val) {
      if (val === "OUT") {
        this.dataForm.calculationMethod = "AFTER";
      }
      this.dataForm.attendanceType = null;
      this.dataForm.configKey = "";
      this.dataForm.configValue = null;
      this.dataForm.configTime = null;
      this.dataForm.configList = [];
    },

    resetAttendanceMethod() {
      this.dataForm.attendanceType = null;
      this.dataForm.configKey = "";
      this.dataForm.configValue = null;
      this.dataForm.configTime = null;
    },

    changeAttendanceTypeMethod(val) {
      this.dataForm.configKey = "";
      this.dataForm.configValue = null;
      this.dataForm.configTime = null;
      this.dataForm.configList = [];
      if (val === "LATE") {
        this.dataForm.configList.push({ configName: "", configStartTime: "", configEndTime: "", configValue: 0 });
      }
    },

    onAppliedTimeTypeChange() {
      MONTH_LIST.forEach(m => { this.dataForm[m.key] = false; });
      // this.dataForm.defaultCycle = true;
      // this.dataForm.startDay = 1; this.dataForm.endDay = 31;
      this.dataForm.startDate = null; this.dataForm.endDate = null;
      this.dataForm.numberUnit = null;
    },

    onMappingAliasInput(mapping) {
      // Normalize alias về 1 ký tự A-Z để dễ dùng trong biểu thức
      mapping.alias = String(mapping.alias || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 1);
      this.$nextTick(() => this.$refs.dataForm?.validateField("formulaExpression"));
    },

    // ─── formulaMappings helpers ─────────────────────────────────────
    _nextAlias() {
      // Return next available single-letter alias from A..Z not used in current mappings
      const used = this.dataForm.formulaMappings
        .map(m => String(m.alias || '').trim().toUpperCase())
        .filter(Boolean);
      for (let i = 0; i < 26; i++) {
        const ch = String.fromCharCode(65 + i); // A..Z
        if (!used.includes(ch)) return ch;
      }
      return '';
    },

    addMapping() {
      const alias = this._nextAlias();
      this.dataForm.formulaMappings.push({ alias: alias || '', idPrincipalRevenue: null });
      this.$nextTick(() => this.$refs.dataForm?.validateField("formulaExpression"));
    },
    removeMapping(idx) {
      this.dataForm.formulaMappings.splice(idx, 1);
      this.$nextTick(() => this.$refs.dataForm?.validateField("formulaExpression"));
    },

    // ─── configList helpers ──────────────────────────────────────────
    addConfig() {
      this.dataForm.configList.push({ configName: "", configStartTime: "", configEndTime: "", configValue: 0 });
    },
    removeConfig(idx) {
      this.dataForm.configList.splice(idx, 1);
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("vi-VN") + " ₫";
    },

    _getInferredAttendanceType(d) {
      if (d.attendanceType) return d.attendanceType;
      if (!d.attendance) return null;

      const configKey = d.configKey;
      if (configKey) {
        if (CONFIG_KEY_IN_BEFORE_LEARN.some(item => item.key === configKey) ||
            CONFIG_KEY_IN_AFTER_LEARN.some(item => item.key === configKey) ||
            CONFIG_KEY_OUT_AFTER_LEARN.some(item => item.key === configKey)) {
          return "LEARN";
        }
        if (CONFIG_KEY_IN_BEFORE_EAT.some(item => item.key === configKey) ||
            CONFIG_KEY_OUT_AFTER_EAT.some(item => item.key === configKey)) {
          return "EAT";
        }
      } else if (d.configList && d.configList.length > 0) {
        return "LATE";
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog";

/deep/ .el-input-number.sk-u-w-full { width: 100%; }
/deep/ .el-date-editor.sk-u-w-full  { width: 100%; }

/* Ô Thành tiền (read-only, FE preview) */
.trf-preview-money {
  padding: 10px 12px;
  width: 100%;
  text-align: right;
  background: #f0f4f8;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-weight: 700;
  font-size: 15px;
  color: #1d4ed8;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
}

/* Công thức đã dịch (read-only) */
.trf-formula-name {
  padding: 10px 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #15803d;
  font-weight: 500;
  word-break: break-all;
}

/* Icon help */
.trf-help-icon {
  color: #64748b;
  font-size: 14px;
  vertical-align: middle;
}

/* Discount amount display */
.trf-discount-amount {
  color: #dc2626;
  font-weight: 700;
  min-width: 120px;
  text-align: right;
  white-space: nowrap;
}

/* Month checkbox grid */
.trf-month-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.trf-month-cb {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Mapping row */
.trf-mapping-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
}

/* Config row */
.trf-config-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
}

/* Header labels */
.trf-mapping-header,
.trf-config-header {
  padding: 0 10px;
}
.trf-col-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

/* Empty hint */
.trf-empty-hint {
  color: #94a3b8;
  font-style: italic;
  font-size: 13px;
  margin-bottom: 8px;
}

.trf-formula-warning {
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
}

.trf-help-entry {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.trf-update-note {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.45;
  border: 1px solid transparent;
}

.trf-update-note i {
  font-size: 15px;
  margin-top: 2px;
}

.trf-update-note--apply {
  color: #166534;
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.trf-update-note--skip {
  color: #9a3412;
  background: #fff7ed;
  border-color: #fed7aa;
}

</style>
